import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../store/gameStore';

/**
 * Top-left identity card — kept deliberately quiet.
 *
 * Shows: identity dot, custom name, current mass, boost meter, best score,
 * and a row of active-powerup chips when any are active.
 *
 * Reads `goldBorderActive` directly from the store so the gold trophy
 * indicator appears the instant the 60s reward fires — without
 * threading another prop through HUD.tsx.
 */
export function IdentityCard({
  mass,
  bestMass,
  playerHue,
  playerName,
  surgeRemaining = 0,
  doubleRemaining = 0,
  shielded = false,
  kills = 0,
}: {
  mass: number;
  bestMass: number;
  playerHue: number;
  playerName: string;
  surgeRemaining?: number;
  doubleRemaining?: number;
  shielded?: boolean;
  kills?: number;
}) {
  const goldBorderActive = useGameStore((s) => s.goldBorderActive);
  const playerColor = `hsl(${playerHue} 100% 65%)`;
  const BOOST_FLOOR = 220;
  const boostPct = Math.max(0, Math.min(1, (mass - BOOST_FLOOR) / Math.max(mass, BOOST_FLOOR)));
  const label = playerName || 'YOU';

  // Personal-best surpassed: when the *current* run's mass first exceeds
  // the previous bestMass, flash a "NEW BEST" chip for ~2.6s. Edge-
  // triggered via a ref so we don't keep firing while the player stays
  // above the line. Suppressed when bestMass was zero (first-ever run —
  // no "surpassed" framing makes sense before there's a baseline).
  const [showNewBest, setShowNewBest] = useState(false);
  const baselineRef = useRef<number>(bestMass);
  useEffect(() => {
    // First mount: lock in the baseline for this run. Don't celebrate
    // hitting it on the same tick (that'd fire on the first frame of a
    // respawn when bestMass is non-zero).
    if (baselineRef.current === 0 && bestMass > 0) baselineRef.current = bestMass;
    if (baselineRef.current === 0) return;
    if (mass > baselineRef.current) {
      setShowNewBest(true);
      // Bump the baseline so the chip only fires once per run, not on
      // every pellet eaten past the threshold.
      baselineRef.current = Number.POSITIVE_INFINITY;
      const t = window.setTimeout(() => setShowNewBest(false), 2600);
      return () => clearTimeout(t);
    }
  }, [mass, bestMass]);

  return (
    <div className="hud-panel absolute top-3 left-3 px-2.5 py-1.5 pointer-events-auto w-[min(38vw,clamp(150px,12vw,220px))]">
      <div className="flex items-center gap-1.5 mb-1">
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ background: playerColor, boxShadow: `0 0 5px ${playerColor}` }}
        />
        <span className="neon-text-cyan text-[clamp(9px,0.6vw,12px)] uppercase tracking-[0.22em] flex-1 truncate">
          {label}
        </span>
        {/* Gold-status indicator — appears the instant the 60s reward
            fires. Mirrors the gold halo on the snake's body so a player
            looking at their HUD sees the same crown they see in the
            world. Tooltip surfaces the meaning for new players. */}
        {goldBorderActive && (
          <motion.span
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 480, damping: 18 }}
            className="text-[clamp(10px,0.7vw,13px)] cursor-default"
            style={{
              filter:
                'drop-shadow(0 0 4px rgba(255, 210, 80, 0.95)) drop-shadow(0 0 10px rgba(255, 180, 50, 0.55))',
            }}
            title="60-Second Survivor reward — Gold Border active this session"
            aria-label="Gold Border reward active"
          >
            🏆
          </motion.span>
        )}
        <span
          className="tabular-nums font-semibold text-[clamp(12px,0.9vw,16px)]"
          style={{ color: playerColor, textShadow: `0 0 5px ${playerColor}55` }}
        >
          {Math.round(mass)}
        </span>
      </div>

      <div className="h-1 rounded-full bg-white/8 overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-200 ease-out"
          style={{
            width: `${boostPct * 100}%`,
            background: `linear-gradient(90deg, ${playerColor}, hsl(${(playerHue + 30) % 360} 100% 70%))`,
            boxShadow: `0 0 4px ${playerColor}`,
          }}
        />
      </div>

      {/* Next-milestone progress.
         The retention audit identified the 30s-1min bucket as losing
         66% of players — a window where they have NO visible goal
         between starter pellets and the first milestone toast.
         This row surfaces the next mass milestone + a thin progress
         bar so every pellet feels like measurable progress toward
         a concrete number. Hidden once the player passes the top
         milestone (25k). */}
      <NextMilestone mass={mass} playerColor={playerColor} />

      {(bestMass > 0 || kills > 0) && (
        <div className="mt-1 flex items-center justify-between gap-3 text-[clamp(8px,0.55vw,11px)] uppercase tracking-[0.18em]">
          {bestMass > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="text-white/35">best</span>
              <span className="text-white/65 tabular-nums">{bestMass}</span>
            </span>
          )}
          {kills > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="text-white/35">kills</span>
              <span className="text-white/85 tabular-nums">{kills}</span>
            </span>
          )}
        </div>
      )}

      {/* Active powerup chips — only render the ones currently active */}
      {(surgeRemaining > 0 || doubleRemaining > 0 || shielded) && (
        <div className="mt-1.5 flex items-center gap-1 flex-wrap">
          {surgeRemaining > 0 && (
            <PowerupChip
              icon="⚡"
              color="#5ec8ff"
              label={surgeRemaining.toFixed(1) + 's'}
            />
          )}
          {doubleRemaining > 0 && (
            <PowerupChip
              icon="2×"
              color="#ffd23d"
              label={doubleRemaining.toFixed(1) + 's'}
            />
          )}
          {shielded && (
            <PowerupChip
              icon="◆"
              color="#ff3df0"
              label="shield"
            />
          )}
        </div>
      )}

      {/* NEW BEST chip — slides out the right edge of the card so it
          doesn't push the rest of the layout around. Auto-clears after
          ~2.6s. Color-coded to the player's hue. */}
      <AnimatePresence>
        {showNewBest && (
          <motion.div
            key="newbest"
            initial={{ opacity: 0, x: -6, scale: 0.92 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 8, scale: 0.94 }}
            transition={{ type: 'spring', stiffness: 380, damping: 26 }}
            className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md text-[9px] uppercase tracking-[0.22em] font-semibold whitespace-nowrap pointer-events-none"
            style={{
              color: '#fff',
              background: `linear-gradient(135deg, ${playerColor}, hsl(${(playerHue + 30) % 360} 100% 65%))`,
              boxShadow: `0 0 14px ${playerColor}aa, 0 0 28px ${playerColor}55`,
            }}
            aria-live="polite"
          >
            ★ new best
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PowerupChip({ icon, color, label }: { icon: string; color: string; label: string }) {
  return (
    <div
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] tabular-nums tracking-[0.12em]"
      style={{
        background: `${color}1f`,
        border: `1px solid ${color}66`,
        boxShadow: `0 0 6px ${color}44`,
      }}
    >
      <span className="font-bold" style={{ color }}>{icon}</span>
      <span className="text-white/75">{label}</span>
    </div>
  );
}

/** Compact "next: X" progress row. Single thin bar in the player's
 *  hue + a small numeric label. Mirrors the server-side
 *  MASS_MILESTONES array so the goals match what the server
 *  actually celebrates with a toast (100/250/500/1k/2.5k/5k/10k/25k). */
function NextMilestone({ mass, playerColor }: { mass: number; playerColor: string }): React.ReactElement | null {
  const MILESTONES = [100, 250, 500, 1000, 2500, 5000, 10000, 25000];
  const next = MILESTONES.find((m) => mass < m);
  if (next === undefined) return null;
  const prev = MILESTONES[Math.max(0, MILESTONES.indexOf(next) - 1)] ?? 0;
  const span = next - prev;
  const pct = Math.max(0, Math.min(1, (mass - prev) / span));
  const remaining = Math.max(0, Math.ceil(next - mass));
  return (
    <div className="mt-1.5">
      <div className="flex items-center justify-between text-[clamp(8px,0.55vw,10px)] uppercase tracking-[0.18em] mb-0.5">
        <span className="text-white/35">next</span>
        <span className="text-white/65 tabular-nums">
          +{remaining.toLocaleString()} / {next.toLocaleString()}
        </span>
      </div>
      <div className="h-[2px] rounded-full bg-white/8 overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-200 ease-out"
          style={{
            width: `${pct * 100}%`,
            background: playerColor,
            boxShadow: `0 0 5px ${playerColor}cc`,
          }}
        />
      </div>
    </div>
  );
}
